import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Textarea } from "./ui/textarea";
import { ArrowLeft, Plus, Trash2, Building, Truck, Calendar, FileText } from "lucide-react";
import { toast } from "sonner";
import { branchApiService, Branch } from '../services/branchApi';
import { itemCategoryApiService, ItemCategory } from '../services/itemCategoryApi';
import { rawItemApiService, RawItem } from '../services/rawItemApi';
import { endgoodApiService, Endgood } from '../services/endgoodApi';
import { workOrderApiService, WorkOrder } from '../services/workOrderApi';
import { challanApiService, ChallanMaster, ChallanMasterCreateRequest, ChallanMasterUpdateRequest, ChallanDetailCreateRequest, ChallanStatus, JobType } from '../services/challanApi';
import { grnApiService } from '../services/grnApi';
import { formatDateTimeForAPI, formatDateTimeForInput } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';

interface ChallanItem {
  id: string;
  itemCatId?: number;
  itemId?: number;
  category: string;
  itemName: string;
  heatNo: string;
  // Keep qty as a string so users can type decimals naturally (e.g. "3.", "3.5")
  // Parse to number only for validation/calculations/submission.
  qty: string;
  rate: number;
  nop: number; // Number of pieces
  hsn: string;
  igst: number;
  cgst: number;
  sgst: number;
  tax: number;
  amount: number;
  jobType: JobType;
}

interface ChallanFormData {
  transferType: 'PLANT_TO_PLANT' | 'PLANT_TO_CUSTOMER';
  toBranchId: number | null;
  toBranchAddress: string;
  documentNo: string;
  modeOfDispatch: string;
  vehNo: string;
  placeOfSupp: string;
  datetimeOfSupply: string;
  items: ChallanItem[];
  grandTotalTax: number;
  totalAmount: number;
  grandTotal: number;
}

export function ChallanForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedBranch } = useBranch();

  const isEditMode = !!id;
  const isNewMode = !id;

  const [formData, setFormData] = useState<ChallanFormData>({
    transferType: 'PLANT_TO_PLANT',
    toBranchId: null,
    toBranchAddress: '',
    documentNo: '',
    modeOfDispatch: '',
    vehNo: '',
    placeOfSupp: '',
    datetimeOfSupply: formatDateTimeForAPI(new Date()),
    items: [],
    grandTotalTax: 0,
    totalAmount: 0,
    grandTotal: 0
  });

  const [branches, setBranches] = useState<Branch[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [rawItems, setRawItems] = useState<RawItem[]>([]);
  const [loadingRawItems, setLoadingRawItems] = useState(false);
  const [endgoods, setEndgoods] = useState<Endgood[]>([]);
  const [loadingEndgoods, setLoadingEndgoods] = useState(false);
  const [loading, setLoading] = useState(false);
  const [heatNumbers, setHeatNumbers] = useState<Record<number, string[]>>({}); // rawItemId -> heat numbers
  const [remainingQuantities, setRemainingQuantities] = useState<Record<string, number>>({}); // heatNo -> remaining quantity
  const [workOrders, setWorkOrders] = useState<Record<number, WorkOrder[]>>({}); // endgoodId -> work orders
  const [loadedChallanToBranch, setLoadedChallanToBranch] = useState<Branch | null>(null); // Store toBranch from loaded challan
  const [loadedChallan, setLoadedChallan] = useState<ChallanMaster | null>(null); // Store full challan object for reference

  const parseQty = (qty: string | undefined | null): number => {
    const n = Number.parseFloat((qty ?? '').toString().trim());
    return Number.isFinite(n) ? n : 0;
  };

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingBranches(true);
        setLoadingCategories(true);
        setLoadingRawItems(true);

        const [branchesData, categoriesData, rawItemsData, endgoodsRes] = await Promise.all([
          branchApiService.getBranches(),
          itemCategoryApiService.getItemCategories(),
          rawItemApiService.getRawItems(),
          endgoodApiService.getEndgoodsIdName()
        ]);

        setBranches(branchesData);
        setCategories(categoriesData);
        setRawItems(rawItemsData);
        setEndgoods(endgoodsRes?.success ? endgoodsRes.data || [] : []);
      } catch (error) {
        // console.error('Error loading data:', error);
        toast.error('Failed to load initial data');
      } finally {
        setLoadingBranches(false);
        setLoadingCategories(false);
        setLoadingRawItems(false);
      }
    };

    loadData();
  }, []);

  // Load challan data for edit mode
  useEffect(() => {
    const loadChallan = async () => {
      if (isEditMode && id) {
        try {
          setLoading(true);
          // console.log('Loading challan with id:', id);
          
          // Load branches first if not already loaded
          let branchesData = branches;
          if (branches.length === 0) {
            branchesData = await branchApiService.getBranches();
            setBranches(branchesData);
          }
          
          const challan = await challanApiService.getChallanById(id);
          // console.log('Challan loaded:', challan);
          // console.log('Challan toBranch:', challan.toBranch);
          
          // Store the full challan object for reference
          setLoadedChallan(challan);
          
          const challanDetails = await challanApiService.getChallanDetails(id);
          // console.log('Challan details loaded:', challanDetails);
          
          // Ensure challanDetails is always an array
          const detailsArray = Array.isArray(challanDetails) ? challanDetails : [];
          // console.log('Number of details:', detailsArray.length);

          const mappedItems = detailsArray.map((detail, index) => {
            // Get tax rates from HSN object directly from the detail
            const hsnData = detail.rawItem?.hsn;
            const hsnCode = hsnData?.hsnCode || '';
            
            // Get tax rates based on state and HSN
            const sameState = isSameState();
            let igst = 0, cgst = 0, sgst = 0;
            
            if (hsnData) {
              if (sameState) {
                // Same state: use CGST and SGST from HSN, IGST = 0
                cgst = hsnData.currentCgstRate || 0;
                sgst = hsnData.currentSgstRate || 0;
                igst = 0;
              } else {
                // Different state: use IGST from HSN, CGST and SGST = 0
                igst = hsnData.currentIgstRate || 0;
                cgst = 0;
                sgst = 0;
              }
            } else {
              // Fallback to default rates if HSN not found
              if (sameState) {
                cgst = 9;
                sgst = 9;
                igst = 0;
              } else {
                igst = 18;
                cgst = 0;
                sgst = 0;
              }
            }
            
            const mappedItem = {
              id: detail.id.toString(),
              itemCatId: detail.itemCatId,
              itemId: detail.itemId,
              category: detail.itemCategory?.categoryName || '',
              itemName: detail.rawItem?.itemName || '',
              heatNo: detail.lotNo || '',
              qty: detail.qty !== undefined && detail.qty !== null ? String(detail.qty) : '',
              rate: detail.itemRate || 0,
              nop: 0, // NOP not in API, defaulting to 0
              hsn: hsnCode,
              igst: igst,
              cgst: cgst,
              sgst: sgst,
              tax: detail.amountTax || 0,
              amount: detail.amount || 0,
              jobType: detail.jobType || JobType.RAW_MATERIAL
            };
            // console.log(`Item ${index}:`, mappedItem);
            return mappedItem;
          });

          // Use toBranch from challan object if available, otherwise find it from branches
          const toBranch = challan.toBranch || branchesData.find(b => b.id === challan.toBranchId);
          // console.log('Resolved toBranch:', toBranch);
          // console.log('toBranch from challan:', challan.toBranch);
          
          // Store the toBranch for display purposes - prioritize the one from API response
          if (challan.toBranch) {
            setLoadedChallanToBranch(challan.toBranch);
            // Also ensure it's in the branches list if not already there
            const existingBranch = branchesData.find(b => b.id === challan.toBranch!.id);
            if (!existingBranch) {
              setBranches([...branchesData, challan.toBranch]);
            }
          } else if (toBranch) {
            setLoadedChallanToBranch(toBranch);
            // Also ensure it's in the branches list if not already there
            if (!branchesData.find(b => b.id === toBranch.id)) {
              setBranches([...branchesData, toBranch]);
            }
          }

          const newFormData: ChallanFormData = {
            transferType: challan.jobWorkerId ? 'PLANT_TO_CUSTOMER' : 'PLANT_TO_PLANT',
            toBranchId: challan.toBranchId,
            toBranchAddress: toBranch?.address || challan.toBranch?.address || '',
            documentNo: challan.documentNo || '',
            modeOfDispatch: challan.modeOfDispatch || '',
            vehNo: challan.vehNo || '',
            placeOfSupp: challan.placeOfSupp || '',
            datetimeOfSupply: challan.datetimeOfSupply || formatDateTimeForAPI(new Date()),
            items: mappedItems,
            grandTotalTax: challan.grandTotalTax || 0,
            totalAmount: challan.totalAmount || 0,
            grandTotal: challan.grandTotal || 0
          };
          
          // console.log('Setting form data:', newFormData);
          setFormData(newFormData);
          // console.log('Form data set successfully');
        } catch (error) {
          // console.error('Error loading challan:', error);
          // console.error('Error details:', error instanceof Error ? error.message : error);
          toast.error('Failed to load challan details');
        } finally {
          setLoading(false);
        }
      }
    };

    // Only load if we have an id and are in edit mode
    if (isEditMode && id) {
      loadChallan();
    }
  }, [id, isEditMode, branches.length]);

  // Check if from and to branches are in the same state
  const isSameState = () => {
    if (!selectedBranch || !formData.toBranchId) return false;
    const fromBranch = branches.find(b => b.id === selectedBranch);
    const toBranch = branches.find(b => b.id === formData.toBranchId);
    if (!fromBranch || !toBranch) return false;
    return fromBranch.stateName === toBranch.stateName;
  };

  // Get tax rates based on state and HSN
  const getTaxRates = (hsnCode?: string) => {
    const sameState = isSameState();
    
    if (!hsnCode) {
      if (sameState) {
        return { igst: 0, cgst: 9, sgst: 9 };
      } else {
        return { igst: 18, cgst: 0, sgst: 0 };
      }
    }

    const rawItem = rawItems.find(item => item.hsn?.hsnCode === hsnCode);
    const hsnData = rawItem?.hsn;

    if (!hsnData) {
      if (sameState) {
        return { igst: 0, cgst: 9, sgst: 9 };
      } else {
        return { igst: 18, cgst: 0, sgst: 0 };
      }
    }

    if (sameState) {
      return {
        igst: 0,
        cgst: hsnData.currentCgstRate,
        sgst: hsnData.currentSgstRate
      };
    } else {
      return {
        igst: hsnData.currentIgstRate,
        cgst: 0,
        sgst: 0
      };
    }
  };

  // Load work orders for an endgood
  const loadWorkOrdersForEndgood = async (endgoodId: number) => {
    if (workOrders[endgoodId]) {
      return; // Already loaded
    }
    
    try {
      // Use BY_ENDGOOD endpoint to get work orders for this endgood
      const response = await workOrderApiService.getWorkOrdersByEndgood(endgoodId.toString(), 1, 100);
      if (response.success && response.data) {
        const endgoodWorkOrders = Array.isArray(response.data) ? response.data : [];
        setWorkOrders(prev => ({
          ...prev,
          [endgoodId]: endgoodWorkOrders
        }));
      }
    } catch (error) {
      // console.error('Error loading work orders for endgood:', error);
      // Don't show toast error, just log it
    }
  };

  // Load heat numbers and remaining quantities for a raw item from consumption API
  const loadHeatNumbers = async (rawItemId: number) => {
    if (heatNumbers[rawItemId]) {
      return; // Already loaded
    }

    try {
      // The consumption API returns data with remainingQuantity field
      const grnDetails = await grnApiService.searchGRNDetailsByRawItems([rawItemId], selectedBranch || undefined);
      
      // Extract unique heat numbers and their remaining quantities from consumption API response
      const heatNoMap = new Map<string, number>();
      grnDetails.forEach((detail: any) => {
        const heatNo = detail.heatNo;
        if (heatNo && heatNo.trim() !== '') {
          // Use remainingQuantity from consumption API response
          const remainingQty = detail.remainingQuantity ?? 0;
          // If same heat number appears multiple times, use the maximum remaining quantity
          if (!heatNoMap.has(heatNo) || heatNoMap.get(heatNo)! < remainingQty) {
            heatNoMap.set(heatNo, remainingQty);
          }
        }
      });

      // Only show heat numbers that have remaining quantity > 0
      const uniqueHeatNumbers = Array.from(heatNoMap.keys()).filter(
        heatNo => (heatNoMap.get(heatNo) ?? 0) > 0
      );
      setHeatNumbers(prev => ({
        ...prev,
        [rawItemId]: uniqueHeatNumbers
      }));

      // Store remaining quantities for each heat number
      const newRemainingQuantities: Record<string, number> = {};
      heatNoMap.forEach((qty, heatNo) => {
        newRemainingQuantities[heatNo] = qty;
      });
      setRemainingQuantities(prev => ({
        ...prev,
        ...newRemainingQuantities
      }));
    } catch (error) {
      // console.error('Error loading heat numbers:', error);
      // Don't show toast as this might be called frequently
    }
  };

  const handleFieldChange = (field: keyof ChallanFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-populate address when branch is selected
    if (field === 'toBranchId') {
      const selectedToBranch = branches.find(b => b.id === value);
      if (selectedToBranch) {
        setFormData(prev => ({
          ...prev,
          toBranchId: value,
          toBranchAddress: selectedToBranch.address || ''
        }));
        // Update tax rates for all items when branch changes
        setTimeout(() => {
          updateTaxRatesForAllItems();
        }, 100);
      }
    }
  };

  const updateTaxRatesForAllItems = () => {
    formData.items.forEach(item => {
      const taxRates = getTaxRates(item.hsn);
      updateItem(item.id, 'igst', taxRates.igst);
      updateItem(item.id, 'cgst', taxRates.cgst);
      updateItem(item.id, 'sgst', taxRates.sgst);
    });
  };

  const addItem = () => {
    const taxRates = getTaxRates();
    const newItem: ChallanItem = {
      id: `item_${Date.now()}_${Math.random()}`,
      category: '',
      itemName: '',
      heatNo: '',
      qty: '',
      rate: 0,
      nop: 0,
      hsn: '',
      igst: taxRates.igst,
      cgst: taxRates.cgst,
      sgst: taxRates.sgst,
      tax: 0,
      amount: 0,
      jobType: JobType.RAW_MATERIAL
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const removeItem = (itemId: string) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
    setTimeout(calculateTotals, 100);
  };

  const updateItem = (itemId: string, field: keyof ChallanItem, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };

          // When item is selected, load heat numbers and populate fields
          if (field === 'itemId' && value) {
            // Check if category is "Internal manufacturing parts" to use endgoods
            if (isInternalManufacturingParts(updatedItem.category)) {
              const selectedEndgood = endgoods.find(e => e.id === value);
              if (selectedEndgood) {
                updatedItem.itemName = selectedEndgood.endgoodName;
                updatedItem.hsn = selectedEndgood.hsn?.hsnCode || '';
                updatedItem.itemCatId = selectedEndgood.endgoodCategory?.id;
                // Note: For endgoods, we don't load heat numbers as they don't have heat numbers
                // Clear heat number if it was set
                updatedItem.heatNo = '';

                // Update tax rates based on HSN
                const taxRates = getTaxRates(updatedItem.hsn);
                updatedItem.igst = taxRates.igst;
                updatedItem.cgst = taxRates.cgst;
                updatedItem.sgst = taxRates.sgst;
              }
            } else {
              // Use raw items for other categories
              const selectedRawItem = rawItems.find(r => r.id === value);
              if (selectedRawItem) {
                updatedItem.itemName = selectedRawItem.itemName;
                updatedItem.hsn = selectedRawItem.hsn?.hsnCode || '';
                updatedItem.itemCatId = selectedRawItem.itemCategory?.id;
                updatedItem.category = selectedRawItem.itemCategory?.categoryName || '';
                
                // Load heat numbers for this item
                loadHeatNumbers(selectedRawItem.id);

                // Update tax rates based on HSN
                const taxRates = getTaxRates(updatedItem.hsn);
                updatedItem.igst = taxRates.igst;
                updatedItem.cgst = taxRates.cgst;
                updatedItem.sgst = taxRates.sgst;
              }
            }
          }


          // Calculate amount and tax
          if (field === 'qty' || field === 'rate') {
            const qty = field === 'qty' ? parseQty(String(value ?? '')) : parseQty(updatedItem.qty);
            const rate = field === 'rate' ? Number(value) : updatedItem.rate;
            updatedItem.amount = qty * rate;

            const sameState = isSameState();
            if (sameState) {
              updatedItem.tax = (updatedItem.amount * (updatedItem.cgst + updatedItem.sgst)) / 100;
            } else {
              updatedItem.tax = (updatedItem.amount * updatedItem.igst) / 100;
            }
          }

          // Recalculate tax when tax rates change
          if (field === 'igst' || field === 'cgst' || field === 'sgst') {
            const sameState = isSameState();
            if (sameState) {
              updatedItem.tax = (updatedItem.amount * (updatedItem.cgst + updatedItem.sgst)) / 100;
            } else {
              updatedItem.tax = (updatedItem.amount * updatedItem.igst) / 100;
            }
          }

          return updatedItem;
        }
        return item;
      })
    }));
    setTimeout(calculateTotals, 100);
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.amount, 0);
    const totalTax = formData.items.reduce((sum, item) => sum + item.tax, 0);
    const grandTotal = subtotal + totalTax;

    setFormData(prev => ({
      ...prev,
      totalAmount: subtotal,
      grandTotalTax: totalTax,
      grandTotal: grandTotal
    }));
  };

  const getFilteredRawItems = (selectedCategory: string, currentItemId?: string) => {
    if (!selectedCategory) return [];
    
    const selectedItemIds = formData.items
      .filter(item => item.itemId && item.id !== currentItemId)
      .map(item => item.itemId);

    return rawItems.filter(item =>
      item.itemCategory?.categoryName === selectedCategory &&
      !selectedItemIds.includes(item.id)
    );
  };

  const getFilteredEndgoods = (currentItemId?: string) => {
    // Show all endgoods when category is "Internal manufacturing parts"
    // Don't filter out already selected items - allow same endgood in multiple rows
    return endgoods;
  };

  const isInternalManufacturingParts = (categoryName: string) => {
    return categoryName?.toLowerCase() === 'internal manufacturing parts';
  };

  const handleCategoryChange = (itemId: string, categoryName: string) => {
    updateItem(itemId, 'category', categoryName);
    updateItem(itemId, 'itemId', undefined);
    updateItem(itemId, 'itemName', '');
    updateItem(itemId, 'hsn', '');
    updateItem(itemId, 'heatNo', '');

    const taxRates = getTaxRates();
    updateItem(itemId, 'igst', taxRates.igst);
    updateItem(itemId, 'cgst', taxRates.cgst);
    updateItem(itemId, 'sgst', taxRates.sgst);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.id) {
      toast.error('User information is required. Please log in again.');
      return;
    }

    if (!selectedBranch) {
      toast.error('Please select a branch from the header');
      return;
    }

    if (!formData.toBranchId) {
      toast.error('Please select a receiver branch');
      return;
    }

    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    // Validate required fields and qty > 0
    const invalidItems = formData.items.filter(item => !item.itemId || !item.heatNo || parseQty(item.qty) <= 0);
    if (invalidItems.length > 0) {
      toast.error('Please fill all required fields for all items (Item, Heat No, Qty > 0)');
      return;
    }

    // Validate that qty does not exceed max (remaining quantity) and is > 0
    const exceededItems = formData.items.filter(item => {
      const qtyNum = parseQty(item.qty);
      if (qtyNum <= 0) {
        return true; // Quantity must be > 0
      }
      if (item.heatNo && remainingQuantities[item.heatNo] !== undefined) {
        return qtyNum > remainingQuantities[item.heatNo];
      }
      return false;
    });

    if (exceededItems.length > 0) {
      const zeroQtyItems = exceededItems.filter(item => parseQty(item.qty) <= 0);
      const maxExceededItems = exceededItems.filter(item => 
        parseQty(item.qty) > 0 && item.heatNo && remainingQuantities[item.heatNo] !== undefined && parseQty(item.qty) > remainingQuantities[item.heatNo]
      );
      
      if (zeroQtyItems.length > 0) {
        const itemNames = zeroQtyItems.map(item => item.itemName || 'Item').join(', ');
        toast.error(`Quantity must be greater than 0 for: ${itemNames}`);
        return;
      }
      
      if (maxExceededItems.length > 0) {
        const itemNames = maxExceededItems.map(item => item.itemName || 'Item').join(', ');
        toast.error(`Quantity exceeds maximum available for: ${itemNames}. Please adjust quantities.`);
        return;
      }
    }

    try {
      setLoading(true);

      // Get from branch details
      const fromBranch = branches.find(b => b.id === selectedBranch);
      if (!fromBranch) {
        toast.error('From branch not found');
        return;
      }

      let createdChallan: ChallanMaster;

      if (isEditMode && id) {
        // Update existing challan - preserve existing challan number and status
        const existingChallan = await challanApiService.getChallanById(id);
        const updateData: ChallanMasterUpdateRequest = {
          id: parseInt(id),
          challanNo: existingChallan.challanNo, // Preserve existing challan number
          status: existingChallan.status, // Preserve existing status
          fromBranchId: selectedBranch,
          toBranchId: formData.toBranchId,
          createdBy: existingChallan.createdBy || user.id, // Preserve original creator
          stateName: fromBranch.stateName,
          vehNo: formData.vehNo,
          placeOfSupp: formData.placeOfSupp,
          datetimeOfSupply: formData.datetimeOfSupply,
          grandTotalTax: formData.grandTotalTax,
          totalAmount: formData.totalAmount,
          grandTotal: formData.grandTotal,
          modeOfDispatch: formData.modeOfDispatch,
          documentNo: formData.documentNo
        };
        createdChallan = await challanApiService.updateChallan(id, updateData);
        
        // Delete existing details and create new ones
        const existingDetails = await challanApiService.getChallanDetails(id);
        for (const detail of existingDetails) {
          await challanApiService.deleteChallanDetail(detail.id.toString());
        }
      } else {
        // Create new challan
        // Generate challan number for new challan
        const challanData: ChallanMasterCreateRequest = {
          status: ChallanStatus.DELIVERED,
          fromBranchId: selectedBranch,
          toBranchId: formData.toBranchId,
          createdBy: user.id,
          stateName: fromBranch.stateName,
          vehNo: formData.vehNo,
          placeOfSupp: formData.placeOfSupp,
          datetimeOfSupply: formData.datetimeOfSupply,
          grandTotalTax: formData.grandTotalTax,
          totalAmount: formData.totalAmount,
          grandTotal: formData.grandTotal,
          modeOfDispatch: formData.modeOfDispatch,
          documentNo: formData.documentNo
        };
        createdChallan = await challanApiService.createChallan(challanData);
      }

      // Create challan details in batch
      const detailDataList: ChallanDetailCreateRequest[] = formData.items.map((item) => ({
        challanId: createdChallan.id,
        itemCatId: item.itemCatId,
        itemId: item.itemId,
        lotNo: item.heatNo,
        qty: parseQty(item.qty),
        itemRate: item.rate,
        amount: item.amount,
        amountTax: item.tax,
        jobType: item.jobType
      }));

      await challanApiService.createChallanDetailsBatch(detailDataList);

      toast.success(isEditMode ? 'Challan updated successfully' : 'Challan created successfully');
      navigate('/challans');
    } catch (error) {
      // console.error('Error saving challan:', error);
      toast.error(isEditMode ? 'Failed to update challan' : 'Failed to create challan');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading challan details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/challans')}
            className="hover:bg-primary-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
              {isEditMode ? 'Edit Challan' : 'Add Challan'}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {isEditMode ? 'Edit challan information' : 'Create a new challan for material transfer'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Receiver Details and Transportation Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Receiver Details */}
          <Card className="border-primary-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary-700" />
                Receiver Details
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              

              <div className="space-y-2">
                <Label className="text-primary-700 font-semibold">Name</Label>
                {isEditMode ? (
                  <div className="p-2 bg-gray-50 rounded-md border border-gray-200">
                    {(() => {
                      const toBranch = branches.find(b => b.id === formData.toBranchId);
                      return toBranch 
                        ? `${toBranch.branchCode} - ${toBranch.branchName}`
                        : formData.toBranchId 
                          ? `Branch ID: ${formData.toBranchId}` 
                          : 'N/A';
                    })()}
                  </div>
                ) : (
                  <Select
                    value={formData.toBranchId?.toString() || ''}
                    onValueChange={(value) => handleFieldChange('toBranchId', parseInt(value))}
                  >
                    <SelectTrigger className="bg-gradient-to-r from-primary-50/30 to-white border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue placeholder="Select Branch" />
                    </SelectTrigger>
                    <SelectContentWithSearch>
                      {loadingBranches ? (
                        <SelectItem value="loading" disabled>Loading branches...</SelectItem>
                      ) : (
                        branches
                          .filter(branch => branch.id !== selectedBranch) // Exclude current branch
                          .map(branch => (
                            <SelectItem key={branch.id} value={branch.id.toString()}>
                              {branch.branchCode} - {branch.branchName}
                            </SelectItem>
                          ))
                      )}
                    </SelectContentWithSearch>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-primary-700 font-semibold">Address</Label>
                {isEditMode ? (
                  <div className="p-2 bg-gray-50 rounded-md border border-gray-200 min-h-[80px]">
                    {formData.toBranchAddress || 'N/A'}
                  </div>
                ) : (
                  <Textarea
                    value={formData.toBranchAddress}
                    onChange={(e) => handleFieldChange('toBranchAddress', e.target.value)}
                    className="bg-gradient-to-r from-primary-50/30 to-white border-primary-200 focus:border-primary-400 focus:ring-primary-400 min-h-[80px]"
                    placeholder="Address will be auto-populated"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-primary-700 font-semibold">Document No</Label>
                <Input
                  value={formData.documentNo}
                  onChange={(e) => handleFieldChange('documentNo', e.target.value)}
                  className="bg-gradient-to-r from-primary-50/30 to-white border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  placeholder="Enter document number"
                />
              </div>
            </CardContent>
          </Card>

          {/* Transportation Details */}
          <Card className="border-primary-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary-700" />
                Transportation Details
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-primary-700 font-semibold">Mode of Dispatch</Label>
                <Input
                  value={formData.modeOfDispatch}
                  onChange={(e) => handleFieldChange('modeOfDispatch', e.target.value)}
                  className="bg-gradient-to-r from-primary-50/30 to-white border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  placeholder="Enter mode of dispatch"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-primary-700 font-semibold">Vehicle No.</Label>
                <Input
                  value={formData.vehNo}
                  onChange={(e) => handleFieldChange('vehNo', e.target.value)}
                  className="bg-gradient-to-r from-primary-50/30 to-white border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  placeholder="Enter vehicle number"
                />
              </div>

              {/* <div className="space-y-2">
                <Label className="text-primary-700 font-semibold">Place of Supp</Label>
                <Input
                  value={formData.placeOfSupp}
                  onChange={(e) => handleFieldChange('placeOfSupp', e.target.value)}
                  className="bg-gradient-to-r from-primary-50/30 to-white border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  placeholder="Enter place of supply"
                />
              </div> */}

              <div className="space-y-2">
                <Label className="text-primary-700 font-semibold">Date & Time of Supp</Label>
                <Input
                  type="datetime-local"
                  value={formData.datetimeOfSupply ? formatDateTimeForInput(formData.datetimeOfSupply) : ''}
                  onChange={(e) => handleFieldChange('datetimeOfSupply', formatDateTimeForAPI(new Date(e.target.value)))}
                  className="bg-gradient-to-r from-primary-50/30 to-white border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Item Details */}
        <Card className="border-primary-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary-700" />
                Item Details
              </CardTitle>
              {(
                <Button
                  type="button"
                  onClick={addItem}
                  className="bg-white text-primary-600 hover:bg-primary-50"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add More
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {formData.items.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No items added yet. Click "Add More" to add items.</p>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6 px-6">
                <div className="min-w-full inline-block align-middle">
                  <Table className="min-w-[1400px]">
                    <TableHeader>
                      <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                        <TableHead className="min-w-[120px]">Category</TableHead>
                        <TableHead className="min-w-[200px]">Item Name</TableHead>
                        <TableHead className="min-w-[120px]">Heat No. / WO No.</TableHead>
                        <TableHead className="min-w-[80px]">Qty</TableHead>
                        <TableHead className="min-w-[100px]">Rate</TableHead>
                        <TableHead className="min-w-[80px]">NOP</TableHead>
                        <TableHead className="min-w-[100px]">Hsn</TableHead>
                        <TableHead className="min-w-[80px]">IGST</TableHead>
                        <TableHead className="min-w-[80px]">CGST</TableHead>
                        <TableHead className="min-w-[80px]">SGST</TableHead>
                        <TableHead className="min-w-[100px]">Tax</TableHead>
                        <TableHead className="min-w-[100px]">Amount</TableHead>
                        <TableHead className="min-w-[80px] text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                  <TableBody>
                    {formData.items.map((item) => (
                    <TableRow key={item.id} className="hover:bg-primary-50/50">
                      <TableCell className="align-top">
                        {isEditMode ? (
                          <div className="p-2 bg-gray-50 rounded-md border border-gray-200 text-sm">
                            {item.category || 'N/A'}
                          </div>
                        ) : (
                          <Select
                              value={item.category}
                              onValueChange={(value) => handleCategoryChange(item.id, value)}
                            >
                              <SelectTrigger className="w-full min-w-[120px] bg-gradient-to-r from-primary-50/30 to-white border-primary-200">
                                <SelectValue placeholder="Category" />
                              </SelectTrigger>
                              <SelectContent>
                                {categories.map(cat => (
                                  <SelectItem key={cat.id} value={cat.categoryName}>
                                    {cat.categoryName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        {isEditMode ? (
                          <div className="p-2 bg-gray-50 rounded-md border border-gray-200 text-sm">
                            {item.itemName || 'N/A'}
                          </div>
                        ) : (
                          <Select
                              value={item.itemId?.toString() || ''}
                              onValueChange={(value) => updateItem(item.id, 'itemId', parseInt(value))}
                              disabled={!item.category}
                            >
                              <SelectTrigger className="w-full min-w-[200px] bg-gradient-to-r from-primary-50/30 to-white border-primary-200">
                                <SelectValue placeholder="Select Item" />
                              </SelectTrigger>
                              <SelectContentWithSearch searchPlaceholder={isInternalManufacturingParts(item.category) ? "Search end goods..." : "Search raw items..."} debounceMs={300}>
                                {isInternalManufacturingParts(item.category) ? (
                                  // Show endgoods for "Internal manufacturing parts" category
                                  getFilteredEndgoods(item.id).map(endgood => (
                                    <SelectItem key={endgood.id} value={endgood.id.toString()}>
                                      {endgood.endgoodName}
                                    </SelectItem>
                                  ))
                                ) : (
                                  // Show raw items for other categories
                                  getFilteredRawItems(item.category, item.id).map(rawItem => (
                                    <SelectItem key={rawItem.id} value={rawItem.id.toString()}>
                                      {rawItem.itemName}
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContentWithSearch>
                            </Select>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        {isEditMode ? (
                          <div className="p-2 bg-gray-50 rounded-md border border-gray-200 text-sm">
                            {item.heatNo || 'N/A'}
                          </div>
                        ) : (
                          <Select
                              value={item.heatNo}
                              onValueChange={(value) => updateItem(item.id, 'heatNo', value)}
                              disabled={!item.itemId}
                            >
                              <SelectTrigger className="w-full min-w-[120px] bg-gradient-to-r from-primary-50/30 to-white border-primary-200">
                                <SelectValue placeholder={isInternalManufacturingParts(item.category) ? "WO No" : "Heat No"} />
                              </SelectTrigger>
                              <SelectContent>
                                {item.itemId ? (
                                  isInternalManufacturingParts(item.category) ? (
                                    // Show work order numbers for endgoods
                                    workOrders[item.itemId]?.map((wo: WorkOrder) => (
                                      <SelectItem key={wo.id} value={wo.workOrderNumber}>
                                        {wo.workOrderNumber}
                                      </SelectItem>
                                    ))
                                  ) : (
                                    // Show heat numbers for raw items
                                    heatNumbers[item.itemId]?.map((heatNo: string) => (
                                      <SelectItem key={heatNo} value={heatNo}>
                                        {heatNo}
                                      </SelectItem>
                                    ))
                                  )
                                ) : null}
                              </SelectContent>
                            </Select>
                        )}
                      </TableCell>
                        <TableCell className="align-top">
                          <div className="space-y-1">
                              <Input
                                type="text"
                                value={item.qty}
                                onChange={(e) => updateItem(item.id, 'qty', e.target.value)}
                                onBlur={() => {
                                  if (!item.heatNo || remainingQuantities[item.heatNo] === undefined) return;
                                  if (!item.qty.trim()) return;
                                  const maxQty = remainingQuantities[item.heatNo];
                                  const qtyNum = parseQty(item.qty);
                                  if (qtyNum > maxQty) {
                                    toast.error(`Quantity cannot exceed maximum available: ${maxQty.toFixed(2)}`);
                                  }
                                }}
                                className={`w-full min-w-[80px] bg-gradient-to-r from-primary-50/30 to-white border-primary-200 ${
                                  ((item.qty.trim() !== '' && parseQty(item.qty) <= 0) ||
                                    (item.heatNo && remainingQuantities[item.heatNo] !== undefined && parseQty(item.qty) > remainingQuantities[item.heatNo]))
                                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                    : ''
                                }`}
                                placeholder="0"
                              />
                              {item.heatNo && remainingQuantities[item.heatNo] !== undefined && (
                                <div className={`text-xs mt-0.5 ${
                                  (item.qty.trim() !== '' && parseQty(item.qty) <= 0) || parseQty(item.qty) > remainingQuantities[item.heatNo]
                                    ? 'text-red-600 font-semibold'
                                    : 'text-gray-500'
                                }`}>
                                  Max: {remainingQuantities[item.heatNo].toFixed(2)}
                                  {parseQty(item.qty) > remainingQuantities[item.heatNo] && (
                                    <span className="ml-1">(Exceeded!)</span>
                                  )}
                                  {item.qty.trim() !== '' && parseQty(item.qty) <= 0 && (
                                    <span className="ml-1">(Must be &gt; 0)</span>
                                  )}
                                </div>
                              )}
                              {(!item.heatNo || remainingQuantities[item.heatNo] === undefined) && item.qty.trim() !== '' && parseQty(item.qty) <= 0 && (
                                <div className="text-xs text-red-600 font-semibold mt-0.5">
                                  Quantity must be greater than 0
                                </div>
                              )}
                            </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <Input
                              type="text"
                              value={item.rate || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Allow only numbers and decimal point
                                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                  updateItem(item.id, 'rate', value === '' ? 0 : parseFloat(value) || 0);
                                }
                              }}
                              className="w-full min-w-[100px] bg-gradient-to-r from-primary-50/30 to-white border-primary-200"
                              placeholder="0"
                            />
                        </TableCell>
                        <TableCell className="align-top">
                          <Input
                              type="text"
                              value={item.nop || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Allow only numbers
                                if (value === '' || /^\d*$/.test(value)) {
                                  updateItem(item.id, 'nop', value === '' ? 0 : parseInt(value) || 0);
                                }
                              }}
                              className="w-full min-w-[80px] bg-gradient-to-r from-primary-50/30 to-white border-primary-200"
                              placeholder="0"
                            />
                        </TableCell>
                        <TableCell className="align-top">
                          <span className="text-sm">{item.hsn || '-'}</span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span className="text-sm">{item.igst}%</span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span className="text-sm">{item.cgst}%</span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span className="text-sm">{item.sgst}%</span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span className="font-medium">₹{item.tax.toFixed(2)}</span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span className="font-medium">₹{item.amount.toFixed(2)}</span>
                        </TableCell>
                        {(
                          <TableCell className="align-top">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(item.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        <Card className="border-primary-200 shadow-lg">
          <CardContent className="pt-6">
            <div className="grid grid-cols-3 gap-6 max-w-2xl ml-auto">
              <div className="space-y-2">
                <Label className="text-gray-600">Grand Total Tax</Label>
                <div className="p-3 bg-gray-100 rounded-md font-semibold text-gray-800">
                  ₹{formData.grandTotalTax.toFixed(2)}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600">Total Amount</Label>
                <div className="p-3 bg-gray-100 rounded-md font-semibold text-gray-800">
                  ₹{formData.totalAmount.toFixed(2)}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600">Grand Total</Label>
                <div className="p-3 bg-gradient-to-r from-primary-100 to-primary-200 rounded-md font-bold text-primary-800">
                  ₹{formData.grandTotal.toFixed(2)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-start">
          <Button
            type="submit"
            disabled={loading}
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg px-8"
          >
            {loading ? 'Saving...' : isEditMode ? 'Update Challan' : 'Submit'}
          </Button>
        </div>
      </form>
    </div>
  );
}

